// src/api/mockApi.js
import { 
  getDataFromLocal, 
  addDataToLocal, 
  updateDataInLocal, 
  deleteDataFromLocal,
  clearDataFromLocal,
  initializeDataIfEmpty 
} from "@/utils/localStorageUtils";

// Import default mock data
import { 
  mockPurchaseOrders, 
  mockInvoices, 
  mockVendors, 
  mockSKUs, 
  mockUsers,
  mockQualityInspections,
  mockHandoverLogs
} from "@/data/mockData";

export const MockAPI = {
  // Purchase Orders
  getPOs: () => {
    return initializeDataIfEmpty('purchaseOrders', mockPurchaseOrders);
  },
  
  createPO: (po) => {
    const newPO = {
      ...po,
      id: Date.now(), // Simple ID generation
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('purchaseOrders', newPO, 'id');
  },
  
  updatePO: (po) => {
    const updatedPO = {
      ...po,
      updatedAt: new Date().toISOString()
    };
    return updateDataInLocal('purchaseOrders', updatedPO, 'id');
  },
  
  deletePO: (poId) => {
    return deleteDataFromLocal('purchaseOrders', poId, 'id');
  },
  
  getPOById: (id) => {
    const pos = getDataFromLocal('purchaseOrders');
    return pos.find(po => po.id === id);
  },

  // Invoices
  getInvoices: () => {
    return initializeDataIfEmpty('invoices', mockInvoices);
  },
  
  createInvoice: (invoice) => {
    const newInvoice = {
      ...invoice,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('invoices', newInvoice, 'id');
  },
  
  updateInvoice: (invoice) => {
    const updatedInvoice = {
      ...invoice,
      updatedAt: new Date().toISOString()
    };
    return updateDataInLocal('invoices', updatedInvoice, 'id');
  },
  
  deleteInvoice: (invoiceId) => {
    return deleteDataFromLocal('invoices', invoiceId, 'id');
  },
  
  getInvoiceById: (id) => {
    const invoices = getDataFromLocal('invoices');
    return invoices.find(invoice => invoice.id === id);
  },

  // Vendors
  getVendors: () => {
    return initializeDataIfEmpty('vendors', mockVendors);
  },
  
  createVendor: (vendor) => {
    const newVendor = {
      ...vendor,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('vendors', newVendor, 'id');
  },
  
  updateVendor: (vendor) => {
    const updatedVendor = {
      ...vendor,
      updatedAt: new Date().toISOString()
    };
    return updateDataInLocal('vendors', updatedVendor, 'id');
  },

  // SKUs
  getSKUs: () => {
    return initializeDataIfEmpty('skus', mockSKUs);
  },
  
  createSKU: (sku) => {
    const newSKU = {
      ...sku,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('skus', newSKU, 'id');
  },
  
  updateSKU: (sku) => {
    const updatedSKU = {
      ...sku,
      updatedAt: new Date().toISOString()
    };
    return updateDataInLocal('skus', updatedSKU, 'id');
  },

  // Users
  getUsers: () => {
    return initializeDataIfEmpty('users', mockUsers);
  },

  // Quality Inspections
  getQualityInspections: () => {
    return initializeDataIfEmpty('qualityInspections', mockQualityInspections);
  },
  
  createQualityInspection: (inspection) => {
    const newInspection = {
      ...inspection,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('qualityInspections', newInspection, 'id');
  },

  // Handover Logs
  getHandoverLogs: () => {
    return initializeDataIfEmpty('handoverLogs', mockHandoverLogs);
  },
  
  createHandoverLog: (log) => {
    const newLog = {
      ...log,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return addDataToLocal('handoverLogs', newLog, 'id');
  },

  // Utility functions
  clearAllData: () => {
    clearDataFromLocal('purchaseOrders');
    clearDataFromLocal('invoices');
    clearDataFromLocal('vendors');
    clearDataFromLocal('skus');
    clearDataFromLocal('users');
    clearDataFromLocal('qualityInspections');
    clearDataFromLocal('handoverLogs');
    console.log('✅ Cleared all data from localStorage');
  },

  resetToMockData: () => {
    clearDataFromLocal('purchaseOrders');
    clearDataFromLocal('invoices');
    clearDataFromLocal('vendors');
    clearDataFromLocal('skus');
    clearDataFromLocal('users');
    clearDataFromLocal('qualityInspections');
    clearDataFromLocal('handoverLogs');
    
    // Re-initialize with mock data
    MockAPI.getPOs();
    MockAPI.getInvoices();
    MockAPI.getVendors();
    MockAPI.getSKUs();
    MockAPI.getUsers();
    MockAPI.getQualityInspections();
    MockAPI.getHandoverLogs();
    
    console.log('✅ Reset all data to mock data');
  },

  // Get storage statistics
  getStorageStats: () => {
    const stats = {};
    const keys = ['purchaseOrders', 'invoices', 'vendors', 'skus', 'users', 'qualityInspections', 'handoverLogs'];
    
    keys.forEach(key => {
      const data = getDataFromLocal(key);
      stats[key] = {
        count: data.length,
        size: JSON.stringify(data).length
      };
    });
    
    return stats;
  }
};

